import React, { useEffect, useState, useRef, useCallback } from "react";
import dayjs from "dayjs";
import Button from "@mui/joy/Button";
import Avatar from "@mui/material/Avatar";
import PhotoModalComp from "./PhotoModalComp";
import Backdrop from "@mui/material/Backdrop";
import PhotoFeedbackComp from "./PhotoFeedbackComp";
import ToastMessage from "../../../../common/ToastMessage";
import useScreenWidth from "../../../../store/useScreenWidth";
import TextAreaCustom from "../../../../common/TextAreaCustom";
import useFeedback from "../../../../store/ReactQueries/ticketfeedback/useFeedback";
import useAddFeedback from "../../../../store/ReactQueries/ticketfeedback/useAddFeedback";
import { styled } from "@mui/system";
import { Modal } from "@mui/base/Modal";
import { Button as PhotoBtn } from '@mui/material';
import { FeedbackType } from "../../../../enums/feedbackType";
import { useUserStore } from "../../../../store/useUserStore";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useScreenHeight } from "../../../../store/useScreenHeight";
import { useDarkModeStore } from "../../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { ModalContent } from "../../../../common/TopBar/Buttons/SearchTopBarModal";
import { TicketImageInterface } from "../../../../models/TicketModule/TicketImage";
import { darkModeBackgroud, darkModeBackgroudInputs } from "../../../../utils/constants";
import { TicketFeedbackInterface } from "../../../../models/TicketModule/TicketFeedback";
import { ticketFeedbackSchema } from "../../../../domain/ValidationSchemas/Ticket/ticketFeedback";

export const StyledBackdrop = styled(Backdrop)(() => ({
  zIndex: 1200,
  pointerEvents: "auto",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
}));

interface NewAccModalProps {
  open: boolean;
  ticketId: string;
  handleClose: () => void;
}

export const FeedbackModal: React.FC<NewAccModalProps> = ({
  open,
  ticketId,
  handleClose,
}) => {

  const isSmallScreen = useScreenWidth();
  const addFeedback = useAddFeedback(ticketId);
  const lastFeedbackRef = useRef<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [seePhotos, setSeePhotos] = useState<boolean>(false);
  const [photos, setPhotos] = useState<TicketImageInterface[]>([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(false);
  const [commentPhotos, setCommentPhotos] = useState<boolean>(false);
  const [selectedFeedback, setSelectedFeedback] = useState<TicketFeedbackInterface>();
  const [form, setForm] = useState<TicketFeedbackInterface>({
    images: [],
    comment: "",
    formImages: [],
    objectId: ticketId,
    feedbackType: FeedbackType.TicketFeedback,
  });

  const { user } = useUserStore();
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const { height: screenHeight, setHeight } = useScreenHeight();
  const { data: feedbackByTicketId, error: feedbackError } = useFeedback(ticketId || "");

  const hasPhotos = form.formImages && form.formImages?.length > 0;
  const handleModalHeight = screenHeight < 800 ? '650px' : "820px";

  const handleAddFeedback = () => {
    setIsBtnDisabled(true);
    const upRow: TicketFeedbackInterface = {
      objectId: form.objectId,
      comment: form.comment,
      formImages: form.formImages || [],
      images: form.images || [],
      feedbackType: FeedbackType.TicketFeedback,
    };

    const isValid = validateSchema(ticketFeedbackSchema, upRow);

    setErrors(isValid.errors);
    if (!isValid.result) return;

    addFeedback.mutate(upRow, {
      onSuccess: () => {
        setPhotos([]);
        setForm({ ...form, comment: "", formImages: [], images: [], });
        ToastMessage.Success();
        setIsBtnDisabled(false);
      },
      onError: (error) => {
        setIsBtnDisabled(false);
        ToastMessage.Failed(error?.message);
      },
    });

  };

  const handleCameraClick = () => {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  };

  const handleOpenPhoto = (feedback: TicketFeedbackInterface, index: number) => {
    setSelectedIndex(index);
    setSelectedFeedback(feedback);
    setCommentPhotos(true);
  };

  const handleFeedbackPhotos = (feedback: TicketFeedbackInterface) => {
    const result = feedback.images;
    if (!result) return null;

    if (result && result.length > 0) {
      if (result.length === 1) return ImagePhoto(feedback, result[0].imageUrl, 0)
      else if (result.length === 2) return <div className="module-form-grid gap-1">
        {result.map((item, index) => ImagePhoto(feedback, item.imageUrl, index))}
      </div>
      else if (result.length > 2) return <div className="module-form-grid gap-1">
        {ImagePhoto(feedback, result[0].imageUrl, 0)}
        <div className="position-relative" role="button" onClick={() => handleOpenPhoto(feedback, 1)}>
          {ImagePhoto(feedback, result[1].imageUrl, 1, .5)}
          <span className="position-absolute fw-bold" style={{ top: 20, right: 35 }}>+{result.length - 1}</span>
        </div>
      </div>
    }
  }

  const handleClosePhotos = useCallback(() => {
    if (!commentPhotos) return;
    setSelectedIndex(0);
    setCommentPhotos(false);
    setSeePhotos(false);
    setSelectedFeedback(undefined);
  }, [commentPhotos]);

  const ImagePhoto = (feedback: TicketFeedbackInterface, image: string, index: number, opacity?: number) => {
    return <PhotoBtn
      key={Math.random()}
      variant="contained"
      onClick={() => handleOpenPhoto(feedback, index)}
      sx={{
        padding: 0,
        width: '90px',
        height: '60px',
        color: '#413420',
        opacity: opacity ?? 1,
        background: '#F3F6F9',
        transition: 'transform 0.3s ease',
        '&:hover': {
          backgroundColor: '#E1E5E8',
          transform: 'scale(1.1)',
        },
      }}
    >
      <img
        src={image}
        alt="Uploaded"
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          borderRadius: '4px',
        }}
      />
    </PhotoBtn>
  }

  const handleCompHeightSize = () => {
    if (screenHeight < 800) return hasPhotos ? 320 : 400;
    else return hasPhotos ? 420 : 500;
  }

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setHeight]);

  useEffect(() => {
    if (feedbackError) ToastMessage.Failed(feedbackError.message);
  }, [feedbackError]);

  useEffect(() => {
    setForm({ ...form, objectId: ticketId, formImages: [], images: [], });
  }, [ticketId]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        lastFeedbackRef.current?.scrollIntoView({ block: "nearest", behavior: "auto" });
      }, 100);
    }
  }, [open, feedbackByTicketId]);

  return <React.Fragment>
    <Modal
      open={open}
      disableAutoFocus
      disableEnforceFocus
      slots={{ backdrop: StyledBackdrop }}
      onClose={handleClose}
    >
      <ModalContent
        sx={{
          ...styledModalContent,
          width: isSmallScreen ? 300 : 800,
          minHeight: handleModalHeight,
          maxHeight: handleModalHeight,
          backgroundColor: darkMode ? darkModeBackgroud : "#F3F3F7",
        }}
      >
        <div className="d-flex flex-column pt-3 pb-3">
          <h4
            className="ms-3 mb-0"
            style={{ color: darkMode ? "white" : "#32475C99" }}
          >
            {language["Feedbacks"]}
          </h4>
          <hr style={darkMode ? { borderColor: "white", opacity: 1 } : {}} />
          <div>
            <div
              className="d-flex flex-column p-4 gap-2"
              style={{
                overflowY: "auto",
                height: handleCompHeightSize(),
                maxHeight: handleCompHeightSize(),
              }}
            >
              {feedbackByTicketId?.map((feedback, index) => {
                const isAuthor =
                  user
                  && user.userId
                  && feedback.author
                  && user.userId === feedback.author.id;
                return (
                  <div
                    key={`${feedback.id}-${index}feedback`}
                    title={
                      feedback.author
                        ? `${feedback.author.firstname ?? ""} ${feedback.author.lastname ?? ""
                        }`
                        : ""
                    }
                    ref={
                      index === feedbackByTicketId.length - 1
                        ? lastFeedbackRef
                        : null
                    }
                    className={`d-flex ${isAuthor ? "flex-row-reverse ms-auto" : "flex-row"
                      } gap-2 w-50`}
                  >
                    <Avatar
                      className="mt-4"
                      sx={{ background: "#E7E7FF", color: "#696CFF" }}
                    >
                      {feedback.author && feedback.author.firstname
                        ? feedback.author.firstname.slice(0, 1)
                        : "A"}
                    </Avatar>
                    <div className="position-relative card-feedback">
                      <p
                        className={`mb-0 mt-1 ${isAuthor ? "text-end" : "text-start"
                          }`}
                        style={{ color: "#C9C1D0", fontSize: "11px" }}
                      >
                        {feedback.author
                          ? `${feedback.author.firstname ?? ""} ${feedback.author.lastname ?? ""}`
                          : ""}
                      </p>
                      {
                        feedback.comment
                        && feedback.comment.length > 0
                        && <p
                          className="p-3 mb-0"
                          style={{
                            borderRadius: "10px",
                            backgroundColor: isAuthor ? "#cf93fa" : "white",
                            color: isAuthor ? "white" : "black",
                          }}
                        >
                          {feedback.comment}
                        </p>
                      }
                      <div className="mt-2">
                        {handleFeedbackPhotos(feedback)}
                      </div>

                      <p
                        className={`mb-0 mt-1 ${isAuthor ? "text-end" : "text-start"
                          }`}
                        style={{ color: "#C9C1D0", fontSize: "11px" }}
                      >
                        {
                          feedback.createdDate
                            ? dayjs(feedback.createdDate).format("DD MMM YYYY HH:mm")
                            : dayjs(feedback.updatedDate).format("DD MMM YYYY HH:mm")
                        }
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              className="p-4 pt-0 pb-0"
              style={{ height: hasPhotos ? 230 : 160 }}
            >
              <PhotoFeedbackComp
                id={form.id ?? ""}
                form={form}
                photos={photos}
                className="mt-2 pt-2 pb-2"
                selectedIndex={selectedIndex}
                setForm={setForm}
                setPhotos={setPhotos}
                setSelectedIndex={setSelectedIndex}
              />
              <TextAreaCustom
                name="comment"
                form={form}
                view={false}
                errors={errors}
                className='mt-2'
                isFeedbackMultitext={true}
                isBtnDisabled={isBtnDisabled}
                setForm={setForm}
                handleAddFeedback={handleAddFeedback}
                handleCameraClick={handleCameraClick}
              />
              <p className="text-danger" style={{ fontSize: "12px" }}>
                {errors?.["comment"]}
              </p>
            </div>
          </div>
          <hr style={darkMode ? { borderColor: "white", opacity: 1 } : {}} />
          <div className="d-flex ms-auto me-2">
            <Button
              sx={{
                width: "150px",
                fontWeight: 700,
                fontSize: "16px",
                border: `1px solid var(--btn-color)`,
                color: darkMode ? 'white' : "var(--btn-color)",
                background: darkMode ? darkModeBackgroudInputs : "white",
                "&:hover": {
                  background: darkMode ? darkModeBackgroudInputs : "white",
                  opacity: .7
                },
              }}
              onClick={handleClose}
            >
              {language["End"]}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
    {
      seePhotos
      && <PhotoModalComp
        photos={photos}
        open={seePhotos}
        selectedIndex={selectedIndex}
        handleClose={handleClosePhotos}
      />
    }
    {
      commentPhotos
      && selectedFeedback
      && selectedFeedback.images
      && selectedFeedback.images?.length > 0
      && <PhotoModalComp
        selectedIndex={selectedIndex}
        photos={selectedFeedback.images}
        open={commentPhotos}
        handleClose={handleClosePhotos}
      />
    }
  </React.Fragment>
};
export default FeedbackModal;

const styledModalContent = {
  top: "50%",
  left: "50%",
  zIndex: 1900,
  minHeight: "820px",
  maxHeight: "820px",
  position: "fixed",
  transform: "translate(-50%, -50%)",
};